import { _ } from "../../../polyglot/polyglot.js";
import { makeDropdown } from "../../makeRoadmapStepContent.js";
import {
	notBlank,
	rangeValue,
	validateAll,
	validatorWithLastError,
	validInteger,
} from "../../../widgets/forms/validators.js";
import AbstractRoadmapStep from "../../AbstractRoadmapStep.js";
import inputFieldWidget from "../../../widgets/forms/fields/inputFieldWidget.js";
import ToggableWidget from "../../../widgets/ToggableWidget/ToggableWidget.js";

const dropdownNumbers = [12, 24, 36, 48, 60];
const customValue = "custom";

/**
 * This allows the user to choose the number of columns with actual
 * data (i.e., reference periods) and the number of columns with
 * predicted data (i.e., predicted periods).
 */
export default class NumberOfPeriodsStep extends AbstractRoadmapStep {
	constructor() {
		super(...arguments);

		this._validator = validatorWithLastError(
			validateAll(notBlank(), validInteger(), rangeValue(1, 120)),
		);
	}

	getTitle() {
		return _("Number of periods");
	}

	_getSubtitle() {
		return _(
			"Select the number of months of actual data and forecasted data you would like to view. This will affect the rules you can apply.",
		);
	}

	_isCompleted() {
		const referenceIsValid =
			!this._referencePeriodsRegion?.isVisible() ||
			this._referencePeriodsInput.validate();

		const predictedIsValid =
			!this._predictedPeriodsRegion?.isVisible() ||
			this._predictedPeriodsInput.validate();

		return referenceIsValid && predictedIsValid;
	}

	_getStepContent() {
		return [
			this._makeReferencePeriodsSection(),
			this._makePredictedPeriodsSection(),
		];
	}

	_makeReferencePeriodsSection() {
		const accessor = this._getNumberOfReferencePeriods.bind(this);

		this._referencePeriodsInput = this._makeInput({ accessor });

		this._referencePeriodsRegion = this._makeInputRegion({
			accessor,
			input: this._referencePeriodsInput,
		});

		return this._makeSection({
			title: _("Reference periods"),
			dataTestName: "reference-period-number",
			helpText: _("Choose the number of columns of actual data"),
			inputRegion: this._referencePeriodsRegion,
			accessor,
			mutator: this._setNumberOfReferencePeriods.bind(this),
		});
	}

	_makePredictedPeriodsSection() {
		const accessor = this._getNumberOfPredictedPeriods.bind(this);

		this._predictedPeriodsInput = this._makeInput({ accessor });

		this._predictedPeriodsRegion = this._makeInputRegion({
			accessor,
			input: this._predictedPeriodsInput,
		});

		return this._makeSection({
			title: _("Predicted periods"),
			dataTestName: "predicted-period-number",
			helpText: _("Choose the number of columns of predicted data"),
			inputRegion: this._predictedPeriodsRegion,
			accessor,
			mutator: this._setNumberOfPredictedPeriods.bind(this),
		});
	}

	_getNumberOfReferencePeriods() {
		return this._context.configuration.getNumberOfReferencePeriods();
	}

	_getNumberOfPredictedPeriods() {
		return this._context.configuration.getNumberOfPredictedPeriods();
	}

	_setNumberOfReferencePeriods(value) {
		this._context.configuration.setNumberOfReferencePeriods(value);
	}

	_setNumberOfPredictedPeriods(value) {
		this._context.configuration.setNumberOfPredictedPeriods(value);
	}

	_makeSection({
		title,
		dataTestName,
		helpText,
		inputRegion,
		accessor,
		mutator,
	}) {
		const dropdown = makeDropdown({
			helpText,
			items: this._makePeriodItems({ accessor }),
			onChange: ({ value }) => {
				const isCustom = value === customValue;
				inputRegion.setVisibility(isCustom);
				if (!isCustom) mutator(value);
				this.completed.trigger();
			},
		});

		return this._sectionRenderer({
			dataTestName,
			title,
			dropdown,
			inputRegion,
		});
	}

	_makeInput({ accessor }) {
		return inputFieldWidget({
			attributes: {
				min: 1,
				max: 120,
				mouseup: () => this.completed.trigger(),
			},
			type: "number",
			name: "custom-number",
			value: accessor().toString(),
			"data-test": "input-field",
			validator: this._validator,
			validateWhileTyping: true,
			onKeyUp: () => this.completed.trigger(),
		});
	}

	_makeInputRegion({ accessor, input }) {
		return new ToggableWidget({
			visible: this._isCustomValue(accessor()),
			content: (html) =>
				html.div(
					{ class: "wizard-step-input" },
					_("{numberOfMonths} months", { numberOfMonths: input }),
				),
		});
	}

	_makePeriodItems({ accessor }) {
		const currentValue = accessor();

		const numberItems = dropdownNumbers.map((numberOfMonths) => ({
			value: numberOfMonths,
			selected: numberOfMonths === currentValue,
			name: _("{numberOfMonths} months", { numberOfMonths }),
		}));

		const customItem = {
			value: customValue,
			selected: this._isCustomValue(currentValue),
			name: _("Custom"),
		};

		return [...numberItems, customItem];
	}

	_isCustomValue(value) {
		return value && !dropdownNumbers.includes(value);
	}

	_sectionRenderer({ dataTestName, title, dropdown, inputRegion }) {
		return (html) =>
			html.div(
				{ "data-test": dataTestName },
				(html) => html.div({ class: "title" }, title),
				(html) =>
					html.div({ class: "period" }, dropdown, (html) =>
						html.div({ class: "input-field" }, inputRegion),
					),
			);
	}

	_doSubmit({ onDone }) {
		if (this._referencePeriodsRegion.isVisible())
			this._setNumberOfReferencePeriods(
				+this._referencePeriodsInput.getValue(),
			);

		if (this._predictedPeriodsRegion.isVisible())
			this._setNumberOfPredictedPeriods(
				+this._predictedPeriodsInput.getValue(),
			);

		onDone();
	}
}
